import { useQuery } from "@tanstack/react-query";
import { parsePhoneNumber } from "react-phone-number-input";
import { JobQueryArray } from "../../gatsby-node";
import { JLNamedQuery } from "../../plugins/gatsby-source-flows/gatsby-node";
import { SERPConfig } from "../../plugins/gatsby-source-serp/types";
import type { PossibleGatsbyCountry } from "../live-sites";
import { ConversionEventProps } from "../utils/analytics";
import getCountryCodeFromCountry from "../utils/getCountryCodeFromCountry";
import getUniqueId from "../utils/getUniqueId";

const COUNTRY = process.env.GATSBY_COUNTRY as PossibleGatsbyCountry;
const DOMAIN = process.env.GATSBY_DOMAIN;

const originDomain = `${COUNTRY}.${DOMAIN}`; //eg "us.best-jobs-online.com"
const apiUrlJobs = `https://api-v2.${DOMAIN}/jobs`;
const apiUrlRgDirect = `https://api-v2.${DOMAIN}/rg-direct`;

const countryCode = getCountryCodeFromCountry();

export enum Source {
  path = "path",
  serp = "serp",
  sms = "sms",
}
export type QueryParams = {
  email?: string;
  firstname?: string;
  lastname?: string;
  limit?: number;
  withRgDirect: boolean;
  location?: string;
  offset: number;
  phone?: string;
  position: string;
  source: Source.serp | Source.path;
  sources: JobQueryArray;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
  qrn_campaign?: string;
  qrn_content?: string;
  qrn_medium?: string;
  qrn_source?: string;
  qrn_term?: string;
  gclid?: string;
  queryName?: JLNamedQuery;
  jobid?: string;
};

type QueryParamsWithRGDirect = QueryParams & {
  jobhash?: string;
  jobtitle?: string;
  company?: string;
};

type FetchBody = {
  countryCode: string;
  limit: number;
  location?: string; // TODO this shouldn't be optional
  offset: number;
  originDomain: string;
  position: string;
  source: keyof typeof Source;
  userAgent: Navigator["userAgent"];
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
  dtlStore?: {
    email?: string;
    firstname?: string;
    lastname?: string;
    phonenumber?: string;
  };
  xmlJobStore?: {};
  partnerApi?: {};
  uniqueId?: string;
  gclid?: string;
  queryName?: JLNamedQuery;
  jobid?: string;
};

type FetchBodyRgDirect = {
  company: string;
  countryCode: string;
  jobTitle: string;
  location?: string;
  jobHash?: string;
  gclid?: string;
};

export type Job = {
  id: string;
  cpa?: number;
  url: string;
  rateCurrency: ConversionEventProps["currency"];
  rate: ConversionEventProps["value"];
  impressionurl?: string; // Indeed tracking URL, passed into Aggregator from Indeed XML
  summaryOriginal: string;
  city: string;
  company: string;
  image: string | null;
  placeholderImage: string | null;
  title: string;
  partner: string;

  isHostedApply?: boolean; // @todo - change when we have eligible jobs in the feed
};

function cleanQueryParamsObject({
  email,
  firstname,
  lastname,
  limit,
  location,
  offset,
  phone,
  position,
  source,
  sources,
  utm_campaign,
  utm_content,
  utm_medium,
  utm_source,
  utm_term,
  qrn_campaign,
  qrn_content,
  qrn_medium,
  qrn_source,
  qrn_term,
  gclid,
  jobhash,
  jobtitle,
  company,
  withRgDirect,
  queryName,
  jobid,
}: QueryParamsWithRGDirect) {
  // TODO - don't add keys if the value is undefined
  return {
    email,
    firstname,
    lastname,
    limit,
    location,
    offset,
    phone,
    position,
    source,
    sources,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
    qrn_campaign,
    qrn_content,
    qrn_medium,
    qrn_source,
    qrn_term,
    gclid,
    jobhash,
    jobtitle,
    company,
    withRgDirect,
    queryName,
    jobid,
  };
}

// see https://react-query.tanstack.com/guides/query-keys
export const getQueryKey = (props: QueryParamsWithRGDirect) => {
  const key = [`jobs`, cleanQueryParamsObject(props)];
  return key;
};

export async function fetchJobs(
  queryParams: QueryParamsWithRGDirect,
  positionOverride?: SERPConfig["positionOverride"]
) {
  const {
    location,
    email,
    firstname,
    lastname,
    phone,
    sources,
    offset,
    source = "path",
    limit = 10, // path is limit 10, serp is default 20
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
    qrn_campaign,
    qrn_content,
    qrn_medium,
    qrn_source,
    qrn_term,
    gclid,

    withRgDirect,
    jobhash,
    jobtitle,
    company,

    queryName,

    // jobid,
  } = queryParams;

  let { position } = queryParams;

  if (!position) {
    console.warn("Position is required by the aggregator jobs API.");

    // If there's no position or its falsy, set it to " ".
    // We agreed this was best to submit a search rather than blank screen
    position = " ";
  }

  const params: FetchBody = {
    position,
    countryCode,
    limit,
    offset: source === Source.serp ? offset : 0, // see https://github.com/qrncorporate/ui/issues/712, Path handles offset clientside
    originDomain,
    userAgent: navigator.userAgent,
    uniqueId: getUniqueId(),
    source,
  };

  if (gclid) {
    params.gclid = gclid;
  }

  // TODO this shouldn't be optional
  if (location) {
    params.location = location;
  }

  if (queryName) {
    params.queryName = queryName;
  } else {
    if (sources.includes("dtl")) {
      // Aggregator needs these extra params to decorate the outbound DTL job links
      // So they must be part of the query key (https://tanstack.com/query/v4/docs/guides/query-keys)
      params.dtlStore = { email, firstname, lastname };
      if (phone) {
        const phonenumber = parsePhoneNumber(phone, countryCode);
        if (phonenumber?.nationalNumber) {
          params.dtlStore.phonenumber = phonenumber.nationalNumber; // excludes country code
        }
      }
    }

    if (sources.includes("xml")) {
      params.xmlJobStore = {};
    }

    if (sources.includes("partner")) {
      params.partnerApi = {};
    }
  }

  if (utm_campaign || qrn_campaign) {
    params.utmCampaign = utm_campaign || qrn_campaign;
  }
  if (utm_content || qrn_content) {
    params.utmContent = utm_content || qrn_content;
  }
  if (utm_medium || qrn_medium) {
    params.utmMedium = utm_medium || qrn_medium;
  }
  if (utm_source || qrn_source) {
    params.utmSource = utm_source || qrn_source;
  }
  if (utm_term || qrn_term) {
    params.utmTerm = utm_term || qrn_term;
  }

  // if (jobid) {
  // params.jobid = jobid;
  // params.source = "sms";
  // }

  const body = JSON.stringify(params);

  // Jan 2023
  // We want to fetch a specific job
  // https://us.best-jobs-online.com/path/2003/1/?position=doordash+driver&location=&jobhash=935156287594d2f3&company=DoorDash&jobtitle=Delivery%20Driver
  // jobtitle and company are required, job hash is optional
  if (withRgDirect && (jobtitle || company)) {
    const rgParams: FetchBodyRgDirect = {
      company,
      countryCode,
      location,
      jobTitle: jobtitle,
    };
    if (jobhash) {
      rgParams.jobHash = jobhash;
    }

    if (gclid) {
      rgParams.gclid = gclid;
    }
    const rgParamsQuerystring = Object.keys(rgParams)
      .map((key) => `${key}=${rgParams[key]}`)
      .join("&");

    const apiUrlRG = `${apiUrlRgDirect}?${rgParamsQuerystring}`;

    let [rgFetch, jobsFetch] = await Promise.all([
      fetch(apiUrlRG)
        .then((response) => response.json())
        .catch(() => null),
      fetch(apiUrlJobs, { method: "POST", body }).then((response) =>
        response.json()
      ),
    ]);

    if (source === Source.path) {
      if (rgFetch?.jobs[0]) {
        return truncateSummaries(rgFetch.jobs);
      }
      return truncateSummaries(jobsFetch);
    }
    if (positionOverride && source === Source.serp) {
      jobsFetch = overridePositions(jobsFetch, positionOverride);
    }
    if (!rgFetch) {
      return truncateSummaries(jobsFetch);
    }
    return [].concat(
      truncateSummaries(rgFetch.jobs)?.[0],
      truncateSummaries(jobsFetch)
    );
  }

  const fetchResponse = await fetch(apiUrlJobs, { method: "POST", body });
  let response = await fetchResponse.json();

  // TODO - hacks that should be in the API / server-side
  if (positionOverride && source === Source.serp) {
    response = overridePositions(response, positionOverride);
  }
  // response = exp___decorateWithPromotedJobs(response);
  response = truncateSummaries(response);
  return response;
}

/******** TODO - 👇 MOVE TO SERVER/API 👇 ********/

export function overridePositions(
  response: any,
  positionOverride: SERPConfig["positionOverride"]
) {
  // HACK to make DTL ads appear in hardcoded positions 3 and 10
  if (response && response.length > 0 && positionOverride) {
    const { positions, jobSrc } = positionOverride;

    const dtlIndexes = [];
    // Iterate through the array to find the DTL objects
    for (let i = 0; i < response.length; i++) {
      const item = response[i];

      if (item.src === jobSrc) {
        dtlIndexes.push(i);
        if (dtlIndexes.length === positions.length) {
          // We found all the DTL objects we need, no need to keep iterating
          break;
        }
      }
    }
    const newJobs = [...response];
    // Move the DTL objects to their specified positions if necessary
    for (let i = 0; i < dtlIndexes.length; i++) {
      const index = dtlIndexes[i];
      const desiredPosition = positions[i];

      if (index > desiredPosition) {
        const dtlObject = newJobs.splice(index, 1)[0];
        newJobs.splice(desiredPosition, 0, dtlObject);
      }
    }

    return newJobs;
  }
  return response;
}

function removeHtmlTags(strToSanitize) {
  const myHTML = new DOMParser().parseFromString(strToSanitize, "text/html");
  return myHTML.body.textContent || "";
}

function truncateSummaries(response: any) {
  if (!response || response.length === 0) return response;
  return response.map((job: any) => {
    if (job.summary) {
      job.summaryOriginal = job.summary
        .replace(/<script([\s\S]*?)<\/script>/gi, "")
        .replace(/<a (.*?)href="(.*?)"(.*?)>/g, '<a $1href="#"$3>');
      job.summary = removeHtmlTags(
        job.summary.slice(0, 400).replace(/<br \/>/gi, "  ·  ")
      );
    }
    return job;
  });
}

/******** TODO - 👆 MOVE TO API 👆 ********/

export default function useJobsQuery(
  queryParams: QueryParams,
  positionOverride?: SERPConfig["positionOverride"]
) {
  const queryKey = getQueryKey(queryParams);
  return useQuery(queryKey, () => fetchJobs(queryParams, positionOverride), {
    refetchOnWindowFocus: false,
    enabled: typeof queryParams.location !== "undefined", // TODO - clear this indirection of lack of location meaining the url hasn't been parsed yet. stops fetch happening before url has been parsed, when location is empty as it comes from the query string
    select(data) {
      if (queryParams.source === Source.path && queryParams.offset > 0) {
        // [nov 2024] offset for Path is now handled clientside, see https://github.com/qrncorporate/ui/issues/712
        // queryParams.offset is a number by which to remove the first n items from the array
        return data.slice(queryParams.offset);
      }
      return data;
    },
  });
}
